import { useEffect } from "react";
import { FEATURE_FLAGS } from "@cede/types";
import { useHubUpdates } from "./useHubUpdates";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useHubUpdatesStore } from "@/store/hubUpdatesStore";

// Hook to automatically show hub updates modal
export const useAutoShowHubUpdates = () => {
  const { updates, latestPublishedAt, isLoading } = useHubUpdates();
  const isHubUpdatesModalEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.IMPROVE_UX_HUB_HUB_UPDATES_MODAL);
  const { isOpen, handleOpen, hasSeenUpdate, latestSeenUpdateDate, setLatestSeenForNewUser } = useHubUpdatesStore();

  useEffect(() => {
    // If the feature flag is off, don't show the modal
    if (!isHubUpdatesModalEnabled) return;

    // If there are no updates, don't show the modal
    if (!updates || isLoading || updates.length === 0) return;

    if (!latestPublishedAt) return;

    // For new users (no stored data), mark them as having seen the latest update
    // This prevents new users from seeing old updates
    if (latestSeenUpdateDate === null) {
      setLatestSeenForNewUser(latestPublishedAt);
      return;
    }

    // Check if there are unseen updates (newer than what user has seen)
    const hasUnseenUpdates = updates.some((update) => {
      if (!update.publishedAt) return true;
      return !hasSeenUpdate(new Date(update.publishedAt).toISOString());
    });

    if (hasUnseenUpdates && !isOpen) {
      handleOpen();
    }
  }, [
    isHubUpdatesModalEnabled,
    updates,
    latestPublishedAt,
    isLoading,
    isOpen,
    hasSeenUpdate,
    latestSeenUpdateDate,
    setLatestSeenForNewUser,
    handleOpen,
  ]);

  return {
    shouldShow: isOpen,
    handleClose: useHubUpdatesStore((state) => state.handleClose),
    hasUpdates: updates && updates.length > 0,
  };
};
